/**
 * Typed calls for the dashboard-batch and saved-dashboard endpoints.
 *
 * `src/api/schema.ts` is generated from the *deployed* ACC OpenAPI doc
 * (`generate:api` in package.json), which predates `/statistics` and
 * `/dashboards` entirely — regenerating it will not pick these up until this
 * branch is deployed. The obvious fix, declaration-merging these five paths
 * into the generated `paths` interface so `apiGet`/`apiPost`/etc. from
 * `src/api/client.ts` picked them up automatically, tips `tsc` into
 * "Expression produces a union type that is too complex to represent"
 * (`SuccessJson` in `client.ts`, evaluated over `keyof paths`) — the
 * generated file is already large enough that five more entries cross the
 * compiler's complexity ceiling. So these are dedicated functions instead,
 * built on the same primitives `client.ts` uses (`buildUrl`, `readReason`,
 * `errorForStatus`) rather than its generic `paths`-driven machinery. Once a
 * real `generate:api` run includes these endpoints, call sites can switch
 * back to `apiGet`/`apiPost`/etc. and this file's request plumbing goes away
 * — the domain types below stay either way.
 *
 * Shapes mirror the API's own source: `php/stats_reader.php` (series
 * buckets), `php/api_statistics.php` (batch panels/results),
 * `php/api_dashboards.php` (saved-dashboard rows).
 */

import { buildUrl, readReason } from '@/api/client';
import { errorForStatus, NetworkError } from '@/api/errors';
import { getAccessToken } from '@/features/auth/token';

export type Grain = 'hour' | 'day' | 'week' | 'month' | 'range';
export type RelativeRange = 'last_7_days' | 'last_30_days' | 'this_week' | 'this_month';
export type ExplicitRange = { from: string; to: string };
export type DashboardRange = ExplicitRange | RelativeRange;

export type DashboardTarget =
  { user_id: string } | { department_id: string } | { company_id: string };

export type PanelType = 'series' | 'sessions' | 'availability';

/** A panel as the batch endpoint and saved dashboards both accept it. */
export interface DashboardPanelSpec {
  id: string;
  type: PanelType;
  target: DashboardTarget | DashboardTarget[];
  grain?: Grain;
  range?: DashboardRange;
  metrics?: string[];
  sources?: string[];
  compare_to?: 'previous_period';
  types?: Array<'sleep' | 'workout'>;
  limit?: number;
}

export interface SeriesBucket {
  bucket: string;
  value: number | null;
  sample_count: number;
  sum: number;
  min: number | null;
  max: number | null;
  last: number | null;
  start_unix?: number;
  first_date?: string;
}

export interface SeriesMetric {
  metric_key: string;
  unit: string;
  agg: 'sum' | 'avg' | 'last' | 'max';
  display_scale: number;
  buckets: SeriesBucket[];
}

export interface SessionRow {
  measurement_id: string;
  source_id: string;
  session_type: string;
  subtype: string | null;
  start_time_unix: number;
  end_time_unix: number;
  local_date: string;
  duration_s: number;
  distance_m: number | null;
  calories_kcal: number | null;
  hr_avg: number | null;
  hr_min: number | null;
  hr_max: number | null;
}

export interface AvailabilityRow {
  metric_key: string;
  unit: string;
  agg: string;
  has_hourly: boolean;
  source_id: string;
  first_date: string;
  last_date: string;
  day_count: number;
}

export type PanelDelta = Record<
  string,
  { current: number | null; previous: number | null; change: number | null }
>;

/** One panel's outcome — a comparison panel carries `targets` instead of the fields below. */
export interface DashboardPanelResult {
  status: 'ok' | 'forbidden' | 'insufficient_data';
  target?: DashboardTarget;
  targets?: DashboardPanelResult[];
  grain?: Grain;
  from?: string;
  to?: string;
  series?: SeriesMetric[];
  sessions?: SessionRow[];
  available?: AvailabilityRow[];
  previous?: { from: string; to: string; series: SeriesMetric[] };
  delta?: PanelDelta;
  /** Present only on a `series` panel that requested more than one metric.
   * The panel's own top-level `status` reflects the *union* of every
   * requested metric's contributors clearing the privacy floor — a metric
   * only a handful of people share can still individually be too thin to
   * show safely even though the panel as a whole is `status: ok`. Every
   * requested metric key has its own entry here; one marked
   * `insufficient_data` has no entry in `series`/`previous.series`/`delta`. */
  metric_status?: Record<string, 'ok' | 'insufficient_data'>;
}

export interface DashboardBatchResponse {
  panels: Record<string, DashboardPanelResult>;
}

export interface SavedDashboardSummary {
  id: string;
  name: string;
  created_at: string;
  modified_at: string;
}

export interface SavedDashboard {
  id: string;
  name: string;
  panels: DashboardPanelSpec[];
  created_at?: string;
  modified_at?: string;
}

export interface MetricInfo {
  metric_key: string;
  unit: string;
  agg: 'sum' | 'avg' | 'last' | 'max';
  display_scale: number;
  has_hourly: boolean;
}

export interface MetricsCatalogue {
  metrics: MetricInfo[];
  grains: Grain[];
}

/** Mirrors `client.ts`'s private `request()` — see the file header for why this isn't shared. */
async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  options: { params?: Record<string, string>; body?: unknown } = {},
): Promise<T> {
  const url = buildUrl(path, options.params);

  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : null,
    });
  } catch (cause) {
    throw new NetworkError(`Request to ${path} failed: ${String(cause)}`);
  }

  if (!response.ok) {
    throw errorForStatus(response.status, `${method} ${path} failed`, await readReason(response));
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (text === '') return undefined as T;
  return JSON.parse(text) as T;
}

export function getMetricsCatalogue(): Promise<MetricsCatalogue> {
  return request('GET', '/statistics/metrics');
}

export function runDashboardBatch(panels: DashboardPanelSpec[]): Promise<DashboardBatchResponse> {
  return request('POST', '/statistics/dashboard', { body: { panels } });
}

export function runSavedDashboard(
  dashboardId: string,
  range?: DashboardRange,
): Promise<DashboardBatchResponse> {
  return request('POST', '/statistics/dashboard/{dashboardId}', {
    params: { dashboardId },
    body: range !== undefined ? { range } : {},
  });
}

export function listDashboards(): Promise<{ dashboards: SavedDashboardSummary[] }> {
  return request('GET', '/dashboards');
}

export function createDashboard(
  name: string,
  panels: DashboardPanelSpec[],
): Promise<SavedDashboard> {
  return request('POST', '/dashboards', { body: { name, panels } });
}

export function getDashboard(dashboardId: string): Promise<SavedDashboard> {
  return request('GET', '/dashboards/{dashboardId}', { params: { dashboardId } });
}

export function updateDashboard(
  dashboardId: string,
  name: string,
  panels: DashboardPanelSpec[],
): Promise<SavedDashboard> {
  return request('PUT', '/dashboards/{dashboardId}', {
    params: { dashboardId },
    body: { name, panels },
  });
}

export function deleteDashboard(dashboardId: string): Promise<void> {
  return request('DELETE', '/dashboards/{dashboardId}', { params: { dashboardId } });
}
